import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { nekoPos } from 'public-contracts';
import CampaignService from '../services/campaignService';
import { productsService } from '../services/productsService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Save, X } from 'lucide-react';

type CampaignEntry = nekoPos.CampaignEntry;

const ModernCampaignForm: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const campaignService = useMemo(() => new CampaignService(), []);

  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    targetRevenue?: number;
    targetCategoryRevenue?: number;
    categoryIds?: string[];
    productIds?: string[];
    isActive: boolean;
    metadata?: Record<string, any>;
  }>({
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0], // 90 days from now
    targetRevenue: undefined,
    targetCategoryRevenue: undefined,
    categoryIds: [],
    productIds: [],
    isActive: true,
    metadata: {},
  });

  useEffect(() => {
    loadProductsAndCategories();
  }, []);

  const loadCampaign = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const campaign = await campaignService.getCampaign(id);
      setFormData({
        name: campaign.name,
        description: campaign.description || '',
        startDate: new Date(campaign.startDate).toISOString().split('T')[0],
        endDate: new Date(campaign.endDate).toISOString().split('T')[0],
        targetRevenue: campaign.targetRevenue,
        targetCategoryRevenue: campaign.targetCategoryRevenue,
        categoryIds: campaign.categoryIds || [],
        productIds: campaign.productIds || [],
        isActive: campaign.isActive,
        metadata: campaign.metadata || {},
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.campaigns.load_error')
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit && id) {
      loadCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadProductsAndCategories = async () => {
    try {
      setLoadingProducts(true);
      const [productsData, categoriesData] = await Promise.all([
        productsService.getProducts({ limit: 1000 }),
        productsService.getProductCategories(),
      ]);
      setProducts(Array.isArray(productsData) ? productsData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (err) {
      console.error('Error loading products/categories:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const campaignData: CampaignEntry = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
      };

      if (isEdit && id) {
        await campaignService.updateCampaign(id, campaignData);
      } else {
        await campaignService.createCampaign(campaignData);
      }

      navigate('/campaigns');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.campaigns.save_error')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'targetRevenue' || name === 'targetCategoryRevenue') {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? parseFloat(value) : undefined,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleProductToggle = (productId: string) => {
    setFormData((prev) => {
      const currentIds = prev.productIds || [];
      const newIds = currentIds.includes(productId)
        ? currentIds.filter((id) => id !== productId)
        : [...currentIds, productId];
      return { ...prev, productIds: newIds };
    });
  };

  const handleCategoryToggle = (categoryId: string) => {
    setFormData((prev) => {
      const currentIds = prev.categoryIds || [];
      const newIds = currentIds.includes(categoryId)
        ? currentIds.filter((id) => id !== categoryId)
        : [...currentIds, categoryId];
      return { ...prev, categoryIds: newIds };
    });
  };

  const filteredProducts = products.filter((p) =>
    p.name?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  const filteredCategories = categories.filter((c) =>
    c.name?.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          {translate('pos.campaigns.loading_campaign')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/campaigns')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {translate('pos.common.action.back')}
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit
              ? translate('pos.campaigns.edit_campaign')
              : translate('pos.campaigns.new_campaign')}
          </h1>
          <p className="text-muted-foreground">
            {isEdit
              ? translate('pos.campaigns.update_subtitle')
              : translate('pos.campaigns.create_subtitle')}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>
              {translate('pos.campaigns.campaign_details')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {translate('pos.campaigns.name_required')}
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={translate(
                  'pos.campaigns.name_placeholder'
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                {translate('pos.campaigns.description')}
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={translate(
                  'pos.campaigns.description_placeholder'
                )}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">
                  {translate('pos.campaigns.start_date_required')}
                </Label>
                <Input
                  id="startDate"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">
                  {translate('pos.campaigns.end_date_required')}
                </Label>
                <Input
                  id="endDate"
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="targetRevenue">
                  {translate('pos.campaigns.target_revenue')}
                </Label>
                <Input
                  id="targetRevenue"
                  name="targetRevenue"
                  type="number"
                  value={formData.targetRevenue || ''}
                  onChange={handleChange}
                  placeholder="35000000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="targetCategoryRevenue">
                  {translate('pos.campaigns.target_category_revenue')}
                </Label>
                <Input
                  id="targetCategoryRevenue"
                  name="targetCategoryRevenue"
                  type="number"
                  value={formData.targetCategoryRevenue || ''}
                  onChange={handleChange}
                  placeholder="18000000"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="mr-2"
                />
                {translate('pos.campaigns.active')}
              </Label>
            </div>

            {/* Product Categories Selection */}
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">
                  {translate('pos.campaigns.select_categories')}
                </Label>
                <p className="text-sm text-muted-foreground mb-2">
                  {translate(
                    'pos.campaigns.select_categories_description'
                  )}
                </p>
                <Input
                  placeholder={translate(
                    'pos.campaigns.search_categories'
                  )}
                  value={categorySearchTerm}
                  onChange={(e) => setCategorySearchTerm(e.target.value)}
                  className="mb-2"
                />
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {loadingProducts ? (
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.campaigns.loading_categories')}
                    </div>
                  ) : filteredCategories.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.campaigns.no_categories')}
                    </div>
                  ) : (
                    filteredCategories.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`category-${category.id}`}
                          checked={formData.categoryIds?.includes(category.id)}
                          onCheckedChange={() =>
                            handleCategoryToggle(category.id)
                          }
                        />
                        <Label
                          htmlFor={`category-${category.id}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {category.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
                {formData.categoryIds && formData.categoryIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.categoryIds.map((catId) => {
                      const category = categories.find((c) => c.id === catId);
                      return category ? (
                        <Badge
                          key={catId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {category.name}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => handleCategoryToggle(catId)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Products Selection */}
              <div>
                <Label className="text-base font-semibold">
                  {translate('pos.campaigns.select_products')}
                </Label>
                <p className="text-sm text-muted-foreground mb-2">
                  {translate(
                    'pos.campaigns.select_products_description'
                  )}
                </p>
                <Input
                  placeholder={translate(
                    'pos.campaigns.search_products'
                  )}
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className="mb-2"
                />
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {loadingProducts ? (
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.campaigns.loading_products')}
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.campaigns.no_products')}
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`product-${product.id}`}
                          checked={formData.productIds?.includes(product.id)}
                          onCheckedChange={() =>
                            handleProductToggle(product.id)
                          }
                        />
                        <Label
                          htmlFor={`product-${product.id}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {product.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
                {formData.productIds && formData.productIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.productIds.map((prodId) => {
                      const product = products.find((p) => p.id === prodId);
                      return product ? (
                        <Badge
                          key={prodId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {product.name}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => handleProductToggle(prodId)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving
                  ? translate('pos.campaigns.saving')
                  : isEdit
                  ? translate('pos.campaigns.update_campaign')
                  : translate('pos.campaigns.create_campaign')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/campaigns')}
              >
                {translate('pos.common.action.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default ModernCampaignForm;
